import { useState } from 'react';
import { ApiError, virusCheckApi, type VirusCheckView } from '../../api/client';
import { Button, Card, Chip, Field, LoadError, Loading, Notice } from '../../components/ui';
import { shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → System → Virus check (1.12.16): the checker for files in messages (ClamAV's clamd,
 * an EasyPanel service on Stitch's own servers). With it set up, new files are held until they're
 * checked, files go up to 500 MB, and programs can be shared; while it can't be reached, files wait.
 * Its address is kept here (CLAMAV_URL in EasyPanel works until it's saved here).
 */
export function VirusCheckCard() {
  const data = useData(() => virusCheckApi.get(), []);
  const [address, setAddress] = useState<string | null>(null);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  const v: VirusCheckView | undefined = data.data;

  const act = async (what: string, fn: () => Promise<unknown>) => {
    setBusy(what);
    setNote(null);
    try {
      await fn();
    } catch (err) {
      setNote({ tone: 'crit', text: err instanceof ApiError || err instanceof Error ? err.message : 'That didn’t work.' });
    } finally {
      setBusy('');
    }
  };

  return (
    <Card title="Virus check" action={v ? <Status v={v} /> : undefined}>
      <div className="card-body settings-section">
        <p className="muted settings-lede">
          Every file shared in Messages is checked before anyone can open it, on Stitch’s own servers. With the checker set up, files go up to 500 MB and installers can be shared (with a
          warning). While it can’t be reached, new files wait.
        </p>
        {data.error ? (
          <LoadError error={data.error} retry={data.reload} />
        ) : !v ? (
          <Loading label="Looking at the virus checker" />
        ) : (
          <>
            <dl className="facts">
              <dt>Checker</dt>
              <dd>
                <span className="mono">{v.address ?? 'Not set up'}</span>
                <small className="muted" style={{ display: 'block' }}>
                  {v.source === 'stitch'
                    ? 'Saved in Stitch'
                    : v.source === 'found'
                      ? 'Found automatically: the clamav app in this project'
                      : v.source === 'easypanel'
                        ? 'From EasyPanel (CLAMAV_URL)'
                        : v.looksAt.length
                          ? `Files work as before: up to 25 MB, not checked. Stitch looks for an app named clamav in this project (${v.looksAt[0]}) every minute.`
                          : 'Files work as before: up to 25 MB, not checked'}
                </small>
              </dd>
              {v.engine && (
                <>
                  <dt>Engine</dt>
                  <dd>{v.engine}</dd>
                </>
              )}
              {v.signaturesAt && (
                <>
                  <dt>Signatures</dt>
                  <dd>
                    {shortWhen(v.signaturesAt)}
                    {Date.now() - Date.parse(v.signaturesAt) > 3 * 86_400_000 && (
                      <small className="crit-text" style={{ display: 'block' }}>
                        Over 3 days old: check the checker’s updates (freshclam).
                      </small>
                    )}
                  </dd>
                </>
              )}
              <dt>Waiting to be checked</dt>
              <dd>{v.pending ? `${v.pending} ${v.pending === 1 ? 'file' : 'files'}, the oldest from ${shortWhen(v.oldestPending!)}` : 'None'}</dd>
              {v.lastError && (
                <>
                  <dt>Last problem</dt>
                  <dd className="crit-text">{v.lastError}</dd>
                </>
              )}
            </dl>
            <div className="settings-row">
              <Field
                label="Checker address"
                value={address ?? v.address ?? ''}
                placeholder={v.looksAt[0] ?? 'tcp://clamav:3310'}
                onChange={(e) => setAddress(e.target.value)}
                hint="Only needed when the checker isn’t the clamav app in this project (stitch-clamav.zip), which Stitch finds by itself. ClamAV’s clamd, port 3310."
              />
            </div>
            {note && <Notice tone={note.tone}>{note.text}</Notice>}
            <div className="settings-actions">
              <Button
                variant="primary"
                disabled={busy !== '' || address === null || address.trim() === (v.address ?? '')}
                onClick={() =>
                  void act('save', async () => {
                    await virusCheckApi.save(address?.trim() ? address.trim() : null);
                    setAddress(null);
                    data.reload();
                    setNote({ tone: 'ok', text: 'Saved. Every container uses it within a few seconds.' });
                  })
                }
              >
                {busy === 'save' ? 'Saving…' : 'Save'}
              </Button>
              <Button
                disabled={busy !== '' || (!v.address && !v.looksAt.length)}
                onClick={() =>
                  void act('test', async () => {
                    const r = await virusCheckApi.test();
                    setNote({ tone: r.ok ? 'ok' : 'crit', text: r.message });
                    data.reload();
                  })
                }
              >
                {busy === 'test' ? 'Testing…' : 'Test with a harmless test file'}
              </Button>
              {(v.source === 'stitch' || v.source === 'found') && (
                <Button
                  variant="ghost"
                  disabled={busy !== ''}
                  onClick={() =>
                    void act('clear', async () => {
                      await virusCheckApi.save(null);
                      data.reload();
                    })
                  }
                >
                  Remove from Stitch
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

function Status({ v }: { v: VirusCheckView }) {
  if (!v.address) return <Chip small>Not set up</Chip>;
  if (v.reachable === false) return <Chip small tone="crit">Not answering</Chip>;
  if (v.reachable) return <Chip small tone="ok">Working</Chip>;
  return <Chip small>Not looked at yet</Chip>;
}
